<template>
  <v-app>
    <!-- Desktop: dauerhaft sichtbare Seitenleiste -->
    <v-navigation-drawer app
                         class="noprint sf-nav-drawer"
                         clipped
                         floating
                         permanent
                         v-bind:width="264"
                         v-if="!hideNavigation && $vuetify.breakpoint.lgAndUp">
      <router-view name="navigation" />
    </v-navigation-drawer>

    <v-navigation-drawer app
                         class="noprint sf-nav-drawer"
                         clipped
                         floating
                         right
                         v-bind:width="300"
                         v-if="hasSecondaryNavigation">
      <router-view name="secondaryNavigation" />
    </v-navigation-drawer>

    <v-app-bar app
               class="noprint sf-app-bar"
               clipped-left
               dark
               flat
               height="64"
               v-bind:clipped-right="hasSecondaryNavigation">
      <v-btn class="mr-1"
             icon
             v-bind:aria-expanded="navigationDrawer ? 'true' : 'false'"
             v-bind:title="$t('menu')"
             v-if="!hideNavigation && $vuetify.breakpoint.mdAndDown"
             v-on:click="navigationDrawer = !navigationDrawer">
        <v-icon>{{ navigationDrawer ? 'ti-x' : 'ti-menu-2' }}</v-icon>
      </v-btn>

      <router-link class="sf-brand"
                   v-bind:to="{ name: 'home' }">
        <img alt="SAGASSER"
             class="sf-brand__logo"
             src="/images/logo.png">
        <span class="sf-brand__title hidden-xs-only">{{ company.title }}</span>
      </router-link>

      <v-chip class="ml-4 sf-timer-chip hidden-sm-and-down"
              small
              v-if="showTimer">
        <v-icon left
                small>ti-hourglass</v-icon>
        {{ $t('ends', { value: endTimer }) }}
      </v-chip>

      <v-spacer></v-spacer>

      <locale-block v-bind:locales="locales"
                    v-on:set-locale="setLocale" />

      <v-chip class="ml-2 sf-customer-chip hidden-sm-and-down"
              link
              v-bind:to="{ name: 'home' }"
              v-if="$vuetify.breakpoint.mdAndUp && currentCustomer != null">
        <v-icon left
                small>ti-building-store</v-icon>
        <span class="text-truncate">{{ currentCustomer.id }}</span>
      </v-chip>

      <account-block class="ml-2"
                     v-bind:display-name="displayName"
                     v-bind:email="email"
                     v-bind:is-authenticated="isAuthenticated"
                     v-bind:picture="picture"
                     v-if="!hideSignInButton"
                     v-on:sign-in="signIn"
                     v-on:sign-out="signOut">
        <template v-if="hasRole('Administrator')">
          <v-list-item v-bind:to="{ name: 'administration-dashboard' }">
            <v-list-item-icon class="mr-3">
              <v-icon>ti-shield-check</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ $t('administration') }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-divider class="my-1" />
        </template>
      </account-block>
    </v-app-bar>

    <!-- Mobil/Tablet: Dropdown-Panel unter der App-Leiste -->
    <template v-if="!hideNavigation && $vuetify.breakpoint.mdAndDown">
      <v-fade-transition>
        <div class="sf-mobile-nav__scrim noprint"
             v-if="navigationDrawer"
             v-on:click="navigationDrawer = false"></div>
      </v-fade-transition>
      <v-expand-transition>
        <div class="sf-mobile-nav noprint"
             v-show="navigationDrawer">
          <router-view name="navigation" />
        </div>
      </v-expand-transition>
    </template>

    <v-main>
      <router-view />
    </v-main>

    <v-footer class="noprint sf-footer"
              inset
              padless>
      <v-col class="text-center"
             cols="12">
        <div class="mb-2">© 2021-2027 SAGASSER Unternehmensgruppe</div>
        <v-btn small
               text
               href="https://www.sagasser.de/impressum"
               target="_blank">
          {{ $t('legal') }}
        </v-btn>
        <v-btn small
               text
               href="https://www.sagasser.de/datenschutz"
               target="_blank">
          {{ $t('privacyPolicy') }}
        </v-btn>
      </v-col>
    </v-footer>
  </v-app>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import moment from 'moment'

import locales from '@/locales.json'

import AccountBlock from '@/components/common/AccountBlock'
import LocaleBlock from '@/components/common/LocaleBlock'

export default {
  components: {
    AccountBlock,
    LocaleBlock
  },
  computed: {
    showTimer() {
      if (this.tick) {}
      return moment(this.startDate).isBefore(new Date()) && moment(this.endDate).isAfter(new Date())
    },
    startDate() {
      return this.event.startDate
    },
    endDate() {
      return this.event.endDate
    },
    endTimer() {
      if (this.tick) {}
      return moment(this.endDate).locale(this.$i18n.locale).from(moment.utc(this.currentDate))
    },
    hasSecondaryNavigation() {
      const matched = this.$route.matched

      if (!matched[0] || !matched[0].meta) return false

      return matched[0].meta.hasSecondaryNavigation === true
    },
    hideSignInButton() {
      const matched = this.$route.matched

      if (!matched[0] || !matched[0].meta) return false

      return matched[0].meta.hideSignInButton === true && !this.isAuthenticated
    },
    hideNavigation() {
      const matched = this.$route.matched

      if (!matched[0] || !matched[0].meta) return false

      return matched[0].meta.hideNavigation === true
    },
    ...mapGetters({
      company: 'company',
      currentCustomer: 'currentCustomer',
      displayName: 'auth/displayName',
      email: 'auth/email',
      hasRole: 'auth/hasRole',
      isAuthenticated: 'auth/isAuthenticated',
      picture: 'auth/picture',
      isLive: 'isLive',
      event: 'event'
    })
  },
  created() {
    // TODO: Clear this interval
    setInterval(() => {
      this.tick += 1
    }, 1000)
  },
  data: () => ({
    locales,
    // Mobil: Zustand des Dropdown-Panels (Desktop-Seitenleiste ist immer offen)
    navigationDrawer: false,
    tick: 1
  }),
  methods: {
    setLocale({ localeId }) {
      this.$root.$i18n.locale = localeId
    },
    ...mapActions({
      signIn: 'auth/signIn',
      signOut: 'auth/signOut'
    })
  },
  name: 'App',
  watch: {
    // Panel nach jeder Navigation schließen
    $route() {
      this.navigationDrawer = false
    }
  }
}
</script>

<i18n>
  {
    "de": {
      "administration": "Administration",
      "ends": "endet {value}",
      "legal": "Impressum",
      "menu": "Menü",
      "privacyPolicy": "Datenschutz"
    },
    "en": {
      "administration": "Administration",
      "ends": "ends {value}",
      "legal": "Legal",
      "menu": "Menu",
      "privacyPolicy": "Privacy policy"
    }
  }
</i18n>
